// Lead queries. Everything the list and detail need, typed at the edge so components never
// touch raw rows.

import { useQuery } from '@tanstack/react-query';
import type { FactSource, FindingCategory, FindingState } from '@leadg/shared';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { MOCK_FINDINGS, MOCK_LEADS } from '../../lib/mockData.js';

export interface LeadEntitySummary {
  eik: string | null;
  legal_name: string | null;
  manager_names: string[] | null;
  owner_names: string[] | null;
  match_confidence: number | null;
}

export interface LeadListRow {
  id: number;
  business_name: string;
  vertical_key: string;
  city_key: string;
  area: string | null;
  tier: string | null;
  lead_score: number | null;
  priority: string | null;
  phone: string | null;
  phone_source: FactSource | null;
  name_source: FactSource | null;
  website_url: string | null;
  do_not_contact: boolean;
  domain_norm: string | null;
  parent_lead_id: number | null;
  rating?: number | null;
  review_count?: number | null;
  sales_stage_id?: number | null;
  business_status?: string | null;
  legal_entities?: LeadEntitySummary | null;
  /** Other locations sharing this website. One website is one prospect, one call. */
  branchCount?: number;
}

export interface LeadDetailRow extends LeadListRow {
  address: string | null;
  address_source: FactSource | null;
  domain_norm: string | null;
  normalized_name: string | null;
  google_place_id: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  geo_source: string | null;
  reputation_band: string | null;
  reputation_as_of: string | null;
  score_breakdown: Record<string, unknown> | null;
  my_notes: string;
  created_at: string;
  // Filled from Places since D91 was withdrawn
  rating: number | null;
  review_count: number | null;
  opening_hours: unknown | null;
  business_status: string | null;
  maps_uri: string | null;
}

export interface FindingRow {
  id: number;
  check_key: string;
  category: FindingCategory;
  state: FindingState;
  importance: string | null;
  title_bg: string | null;
  detail_bg: string | null;
  improvement_bg: string | null;
  consequence_bg?: string | null;
  evidence_text?: string | null;
}

export interface PlacesCacheRow {
  name: string | null;
  formatted_address: string | null;
  phone: string | null;
  rating: number | null;
  review_count: number | null;
  fetched_at: string;
}

export interface AuditScreenshotData {
  url: string | null;
  perf: Record<string, unknown> | null;
}

const CLOSED_STATUSES = new Set(['CLOSED_PERMANENTLY', 'REMOVED_FROM_GOOGLE']);

const LIST_COLUMNS =
  'id, business_name, vertical_key, city_key, area, tier, lead_score, priority, phone, phone_source,' +
  ' name_source, website_url, do_not_contact, domain_norm, parent_lead_id, rating, review_count,' +
  ' sales_stage_id, business_status,' +
  ' legal_entities(eik, legal_name, manager_names, owner_names, match_confidence)';

function normalizeEntity(raw: unknown): LeadEntitySummary | null {
  if (!raw) return null;
  const obj = Array.isArray(raw)
    ? (raw[0] as Record<string, unknown> | undefined)
    : (raw as Record<string, unknown>);
  if (!obj || typeof obj !== 'object') return null;
  return {
    eik: (obj['eik'] as string | null) ?? null,
    legal_name: (obj['legal_name'] as string | null) ?? null,
    manager_names: (obj['manager_names'] as string[] | null) ?? null,
    owner_names: (obj['owner_names'] as string[] | null) ?? null,
    match_confidence: (obj['match_confidence'] as number | null) ?? null,
  };
}

function matchesEntitySearch(ent: LeadEntitySummary | null | undefined, lower: string): boolean {
  if (!ent) return false;
  if (ent.eik?.toLowerCase().includes(lower)) return true;
  if (ent.legal_name?.toLowerCase().includes(lower)) return true;
  const names = [...(ent.manager_names ?? []), ...(ent.owner_names ?? [])];
  return names.some((n) => n.toLowerCase().includes(lower));
}

export function matchesLeadSearch(lead: LeadListRow, rawSearch: string): boolean {
  const term = rawSearch.trim();
  if (term === '') return true;
  const lower = term.toLowerCase();
  if (lead.business_name.toLowerCase().includes(lower)) return true;
  if (lead.domain_norm?.toLowerCase().includes(lower)) return true;

  const digits = term.replace(/\D/g, '');
  if (digits.length >= 3) {
    const phoneDigits = (lead.phone ?? '').replace(/\D/g, '');
    if (phoneDigits.includes(digits) || lead.legal_entities?.eik?.includes(digits)) return true;
  }

  return matchesEntitySearch(lead.legal_entities, lower);
}

export function useLeads(search: string) {
  return useQuery({
    queryKey: ['leads', search],
    queryFn: async (): Promise<LeadListRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_LEADS.filter(
          (l) => !CLOSED_STATUSES.has(l.business_status ?? '') && matchesLeadSearch(l, search),
        );
      }

      const { data, error } = await supabase
        .from('leads')
        .select(LIST_COLUMNS)
        .order('lead_score', { ascending: false, nullsFirst: false })
        .order('id', { ascending: true })
        .limit(1000);

      if (error) throw new Error(error.message);

      const all = ((data ?? []) as unknown as Array<Record<string, unknown>>)
        .map((raw) => ({
          ...(raw as unknown as LeadListRow),
          legal_entities: normalizeEntity(raw['legal_entities']),
        }))
        .filter((lead) => !CLOSED_STATUSES.has(lead.business_status ?? ''));

      const branchCounts = new Map<number, number>();
      for (const lead of all) {
        if (lead.parent_lead_id === null) continue;
        branchCounts.set(lead.parent_lead_id, (branchCounts.get(lead.parent_lead_id) ?? 0) + 1);
      }

      return all
        .filter((lead) => lead.parent_lead_id === null && matchesLeadSearch(lead, search))
        .map((lead) => {
          const count = branchCounts.get(lead.id);
          return count === undefined ? lead : { ...lead, branchCount: count };
        });
    },
  });
}

export function useLead(id: number | null) {
  return useQuery({
    queryKey: ['lead', id],
    enabled: id !== null,
    queryFn: async (): Promise<LeadDetailRow | null> => {
      if (!isConfigured || isDemoMode) {
        const found = MOCK_LEADS.find((l) => l.id === id);
        return found ?? null;
      }
      const { data, error } = await supabase.from('leads').select('*').eq('id', id!).maybeSingle();
      if (error) throw new Error(error.message);
      return (data as unknown as LeadDetailRow) ?? null;
    },
  });
}

export function useFindings(leadId: number | null) {
  return useQuery({
    queryKey: ['findings', leadId],
    enabled: leadId !== null,
    queryFn: async (): Promise<FindingRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_FINDINGS[leadId ?? 1] ?? MOCK_FINDINGS[1] ?? [];
      }
      const { data, error } = await supabase
        .from('audit_findings')
        .select(
          'id, check_key, category, state, importance, title_bg, detail_bg, improvement_bg, consequence_bg, evidence_text',
        )
        .eq('lead_id', leadId!);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as FindingRow[];
    },
  });
}

export function useScreenshot(leadId: number | null) {
  return useQuery({
    queryKey: ['audit_screenshot', leadId],
    enabled: leadId !== null,
    queryFn: async (): Promise<AuditScreenshotData | null> => {
      if (!isConfigured || isDemoMode) {
        return null;
      }
      const { data, error } = await supabase
        .from('website_audits')
        .select('screenshot_path, perf')
        .eq('lead_id', leadId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const path = (data.screenshot_path as string | null) ?? null;
      const perf = (data.perf as Record<string, unknown> | null) ?? null;
      if (!path) return { url: null, perf };
      const { data: signed } = await supabase.storage
        .from('audit-screenshots')
        .createSignedUrl(path, 3600);
      return { url: signed?.signedUrl ?? null, perf };
    },
  });
}

export function usePlacesCache(leadId: number | null) {
  return useQuery({
    queryKey: ['places_cache', leadId],
    enabled: leadId !== null,
    queryFn: async (): Promise<PlacesCacheRow | null> => {
      if (!isConfigured || isDemoMode) {
        const lead = MOCK_LEADS.find((l) => l.id === leadId);
        if (!lead) return null;
        return {
          name: lead.business_name,
          formatted_address: lead.address,
          phone: lead.phone,
          rating: lead.rating,
          review_count: lead.review_count,
          fetched_at: lead.created_at,
        };
      }
      const { data, error } = await supabase
        .from('places_cache')
        .select('name, formatted_address, phone, rating, review_count, fetched_at')
        .eq('lead_id', leadId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as unknown as PlacesCacheRow) ?? null;
    },
  });
}
